import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { OidcSecurityService } from "angular-auth-oidc-client";
import Quill from "quill";
import { getCleanedHtml } from "../../../../quill-languagetool";
import { TextvorlageComponent } from "../../../textvorlage/textvorlage.component";
import { buildQuillEditorModules } from "../../quill-editor-config";

type SpeechRecognitionCtor = new () => SpeechRecognition;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  }
}

@Component({
  selector: "app-love-it-textarea-textvorlage",
  templateUrl: "./love-it-textarea-textvorlage.component.html",
  styleUrls: ["./love-it-textarea-textvorlage.component.scss"],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LoveItTextareaTextvorlageComponent),
      multi: true,
    },
  ],
})
export class LoveItTextareaTextvorlageComponent implements OnInit, ControlValueAccessor {
  @Input({ required: true }) formControl!: FormControl;
  @Input({ required: true }) submitted: boolean;
  @Input() textbausteinBezeichnung!: string;
  @Input() label: string;
  @Input() enableFormatting: boolean = false;
  @Output() onTextEdited: EventEmitter<string> = new EventEmitter<string>();

  quillEditorModules: any;
  quillEditor: Quill;

  value: string = "";
  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  listening = false;
  private recognition: SpeechRecognition | null = null;
  private sttInsertIndex: number | null = null;

  constructor(
    private modalController: ModalController,
    private oidcSecurityService: OidcSecurityService,
  ) {}

  ngOnInit() {
    this.quillEditorModules = buildQuillEditorModules(this.oidcSecurityService.getToken(), this.enableFormatting);

    if (!this.enableFormatting && this.formControl.value) {
      const plainText = this.stripHtml(this.formControl.value);
      if (plainText !== this.formControl.value) {
        this.formControl.setValue(plainText, { emitEvent: false });
      }
    }
  }

  get speechToTextAvailable(): boolean {
    return typeof window !== "undefined" && (!!window.SpeechRecognition || !!window.webkitSpeechRecognition);
  }

  toggleSpeechToText(): void {
    if (!this.formControl || this.formControl.disabled || !this.speechToTextAvailable) {
      return;
    }

    if (this.listening) {
      this.recognition?.stop();
      return;
    }

    const Ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!Ctor) {
      return;
    }

    const recognition = new Ctor();
    recognition.lang = "de-AT";
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      this.listening = true;
    };

    recognition.onend = () => {
      this.listening = false;
      this.recognition = null;
      this.sttInsertIndex = null;
    };

    recognition.onerror = () => {
      this.listening = false;
      this.recognition = null;
      this.sttInsertIndex = null;
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const results = Array.from(event.results).slice(event.resultIndex);

      const finalText = results
        .filter((r) => r.isFinal)
        .map((r) => r[0].transcript)
        .join(" ")
        .trim();

      if (!finalText) {
        return;
      }

      if (this.quillEditor) {
        const index = this.sttInsertIndex ?? this.quillEditor.getLength() - 1;
        const textBefore = this.quillEditor.getText(0, index);
        const needsSpace = textBefore.length > 0 && !textBefore.endsWith(" ") && !textBefore.endsWith("\n");
        const textToInsert = needsSpace ? ` ${finalText}` : finalText;

        this.quillEditor.insertText(index, textToInsert);
        this.sttInsertIndex = index + textToInsert.length;

        const currentValue = this.enableFormatting
          ? getCleanedHtml(this.quillEditor.root.innerHTML)
          : this.quillEditor.getText().replace(/\n$/, "");
        this.formControl.setValue(currentValue, { emitModelToViewChange: false });
        this.formControl.markAsDirty();
        this.formControl.markAsTouched();
        this.onTextEdited.emit(currentValue);
      } else {
        const current = String(this.formControl.value ?? "");
        const next = current.trim().length === 0 ? finalText : `${current.trimEnd()} ${finalText}`;

        if (next !== current) {
          this.formControl.setValue(next);
          this.formControl.markAsDirty();
          this.formControl.markAsTouched();
          this.onTextEdited.emit(next);
        }
      }
    };

    this.recognition = recognition;
    this.sttInsertIndex = this.quillEditor?.getSelection()?.index ?? this.quillEditor?.getLength() ?? null;
    recognition.start();
  }

  writeValue(value: string): void {
    this.value = value || "";
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  async openTextbausteinModal(formControl: FormControl, field: string) {
    const modal = await this.modalController.create({
      component: TextvorlageComponent,
      cssClass: ["resizeable-modal", "textvorlage-modal-size"],
      backdropDismiss: false,
      componentProps: {
        content: formControl?.value,
        field,
        enableFormatting: this.enableFormatting,
      },
    });

    modal.onDidDismiss().then((data) => {
      const d = data.data;
      if (d && d.keep) {
        this.setFormControlText(formControl, d.content);
      }
    });

    await modal.present();
  }

  onEditorCreated(editor: Quill) {
    this.quillEditor = editor;
  }

  insertTextAtCursor(newValue: string) {
    if (!this.quillEditor || !newValue) {
      return;
    }
    const selection = this.quillEditor.getSelection(true);
    const index = selection?.index ?? this.quillEditor.getLength();
    let cursorPosition: number;
    if (this.enableFormatting) {
      const lengthBefore = this.quillEditor.getLength();
      this.quillEditor.clipboard.dangerouslyPasteHTML(index, newValue);
      cursorPosition = index + (this.quillEditor.getLength() - lengthBefore);
    } else {
      const plainText = this.stripHtml(newValue);
      this.quillEditor.insertText(index, plainText);
      cursorPosition = index + plainText.length;
    }
    const currentValue = this.enableFormatting
      ? getCleanedHtml(this.quillEditor.root.innerHTML)
      : this.quillEditor.getText().replace(/\n$/, "");
    this.formControl.setValue(currentValue, { emitModelToViewChange: false });
    this.formControl.markAsDirty();
    this.onTextEdited.emit(currentValue);
    setTimeout(() => {
      this.quillEditor.focus();
      this.quillEditor.setSelection(cursorPosition, 0);
    }, 1000);
  }

  setFormControlText(formControl: FormControl, newValue: any) {
    const value = !this.enableFormatting && newValue ? this.stripHtml(newValue) : newValue;
    this.onTextEdited.emit(value);
    formControl.patchValue(value);
    formControl.markAsDirty();
  }

  onQuillContentChanged(event: any) {
    const rawValue = this.formControl.value ?? "";
    this.onTextEdited.emit(this.enableFormatting ? getCleanedHtml(rawValue) : rawValue);
  }

  private stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  }

  protected readonly Validators = Validators;
}
